import { ApiRequest, ShowLoader } from 'GlobalFunctions';
import { useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { initializeSubscription, handleSubscriptionSuccess } from 'components/Subscription/Subscription';
import { enqueueSnackbar } from 'notistack';

import {ProjectConfig} from 'Global';
import { subscribe } from 'diagnostics_channel';

// Import payment forms from Subscription Utils (only Stripe and Square need custom components)
import StripeForm from 'components/Subscription/Utils/StripeForm';
import SquareForm from 'components/Subscription/Utils/SquareForm';

// Store the payment form root reference
let subscriptionPaymentFormRoot: Root | null = null;

/**
 * Generates a unique order code in the format PCS-{timestamp}-{random6chars}
 * Example: PCS-1764134221812-IEQ49R
 */
const generateOrderCode = (): string => {
    const timestamp = Date.now();
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let randomPart = '';
    for (let i = 0; i < 6; i++) {
        randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `PCS-${timestamp}-${randomPart}`;
};

// Add this new function to calculate the next payment date based on billing cycle
const calculateNextPaymentDate = (billingCycle: string): string => {
    const [timePart] = billingCycle.split(":");
    const qty = parseInt(timePart.slice(0, -1), 10);
    const unit = timePart.slice(-1);
    
    const nextDate = new Date();
    
    switch (unit) {
        case "y": 
            nextDate.setFullYear(nextDate.getFullYear() + qty);
            break;
        case "m": 
            nextDate.setMonth(nextDate.getMonth() + qty);
            break;
        case "w": 
            nextDate.setDate(nextDate.getDate() + (qty * 7));
            break;
        case "d": 
            nextDate.setDate(nextDate.getDate() + qty);
            break;
    }
    
    // Format date as "DD Month, YYYY" - explicitly use en-US locale to ensure English month names
    const day = nextDate.getDate();
    const month = nextDate.toLocaleString('en-US', { month: 'long' });
    const year = nextDate.getFullYear();
    
    return `${day} ${month}, ${year}`;
};

// Update the next payment date display
const updateNextPaymentDate = (billingCycle: string): void => {
    const nextPaymentElement = document.querySelector('[pc="next-payment"]');
    if (nextPaymentElement) {
        const nextDate = calculateNextPaymentDate(billingCycle);
        nextPaymentElement.textContent = `Next payment ${nextDate}`;
    }
};

/*=========================================================
GET SUBSCRIPTION TEMPLATE
=========================================================*/
const getSubscriptionTemplate = async (): Promise<string> => {
    let templateHTML = '';
    
    await ApiRequest({
        url: "/templates/getTemplate",
        method: "get",
        query: {
            template_name: "subscription",
        },
        setResponse: (response: any) => {
            if (response.status === 'success' && response.template) {
                templateHTML = response.template;
            } else {
                console.error("Failed to load subscription template:", response);
            }
        }
    });
    
    return templateHTML;
}


const handleClose = (event?: MouseEvent) => {
  const popup = document.querySelector('[pc="subscription"]') as HTMLElement;
  // If event is provided, only close when clicking outside the inner container
  if (event) {
    const popupInner = document.querySelector('[pc="subscription-wrapper"]') as HTMLElement;
    if (popup && popupInner && !popupInner.contains(event.target as Node)) {
      popup.style.display = 'none';
      document.removeEventListener('click', handleClose);
    }
  } else {
    if (popup) {
      popup.style.display = 'none';
      document.removeEventListener('click', handleClose);
    }
  }
};

/**
 * Inserts subscription HTML content based on the subscription mode
 * @param {string} subscriptionHTML - The HTML template to insert
 * @param {string} subscriptionMode - The subscription mode (embed or modal)
 * @returns {HTMLElement|null} - The inserted element or null if insertion failed
 */
const insertSubscriptionContent = (subscriptionHTML: string, subscriptionMode: string): HTMLElement | null => {
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = subscriptionHTML;
    const subscriptionElement = tempContainer.querySelector('[pc="subscription"]') as HTMLElement;
    
    if (!subscriptionElement) return null;
    
    if (subscriptionMode === 'embed') {
        // Hide the add button if it exists
        const addBtn = document.querySelector('[pc="add-btn"]') as HTMLElement;
        if (addBtn) {
            addBtn.style.display = 'none';
        }
        const wrapper = document.querySelector('[pc="sub-embed-container"]');
        if (wrapper) {
            // Only embed the .subscription-wrapper content
            const popupInner = subscriptionElement.querySelector('[pc="subscription-wrapper"]');
            if (popupInner) {
                wrapper.innerHTML = '';
                wrapper.appendChild(popupInner);
                return popupInner as HTMLElement;
            }
            console.error("subscription-wrapper not found for embed mode");
            return null;
        }
        console.error("Subscription wrapper not found for embed mode");
        return null;
    } else {
        // Default: append to body (modal mode)
        // Set display: none immediately after obtaining the template
        subscriptionElement.style.display = 'none';
        subscriptionElement.style.position = 'fixed';
        subscriptionElement.style.top = '0';
        subscriptionElement.style.width = '100%';
        subscriptionElement.style.height = '100%';
        subscriptionElement.style.zIndex = '9999';


        document.body.appendChild(subscriptionElement);
        return subscriptionElement;
    }
};

/**
 * Check if the current product is a subscription type
 * @returns {boolean} True if the product is a subscription
 */
export const checkForSubscription = async () => {

    // Check if the subscription form element exists
    const subscriptionFormEl = document.querySelector('[pc-sub="form"]');
    if (subscriptionFormEl) {
        // Determine subscription mode (default to modal if not specified)
        const subscriptionMode = subscriptionFormEl.getAttribute('pc-sub-mode') || 'modal';
        
        // For modal mode, check if popup already exists
        if (subscriptionMode !== 'embed' && document.querySelector('[pc="subscription"]')) {
            return true;
        }
        
        // For embed mode, check if content is already in wrapper
        if (subscriptionMode === 'embed' && document.querySelector('[pc="subscription-wrapper"] > *')) {
            return true;
        }
        
        // Get the HTML content from the API
        const subscriptionPopupHTML = await getSubscriptionTemplate();
        
        // Insert the content based on the mode
        const subscriptionElement = insertSubscriptionContent(subscriptionPopupHTML, subscriptionMode);
        if (subscriptionElement) {
            // Hide subscription status element initially and ensure form is visible
            const subscriptionStatusEl = subscriptionElement.querySelector('[pc="subscription-status"]') as HTMLElement;
            const subscriptionFormEl = subscriptionElement.querySelector('[pc="subscription-form"]') as HTMLElement;
            
            if (subscriptionStatusEl) {
                subscriptionStatusEl.classList.add("hide_content");
            }
            if (subscriptionFormEl) {
                subscriptionFormEl.classList.remove("hide_content");
            }

            /*=========================================
            POPULATE BILLING CYCLE SELECTS FOR ALL PRODUCTS
            Each product container has its own pc-sub-value and pc-sub-element
            We need to populate each select with its corresponding billing cycles
            =========================================*/
            const populateBillingCycleSelect = (container: Element | Document) => {
                const billingCycleDataEl = container.querySelector('[pc-sub-value]');
                const billingCycleSelectEl = container.querySelector('[pc-sub-element]') as HTMLSelectElement;
                // Find the product price element within this container
                const productPriceEl = container.querySelector('[pc="product-price"]');
                
                // Capture the original price format to preserve currency symbol
                const originalPriceText = productPriceEl?.textContent || '';
                // Extract currency prefix (everything before the number, e.g., "$", "€", "US$")
                const currencyPrefix = originalPriceText.match(/^[^\d]*/)?.[0] || '';
                // Extract currency suffix (everything after the number, e.g., " USD")
                const currencySuffix = originalPriceText.match(/[^\d.]*$/)?.[0] || '';
                
                if (billingCycleDataEl && billingCycleSelectEl && billingCycleDataEl.textContent) {
                    // Hide the billing cycle data element
                    (billingCycleDataEl as HTMLElement).style.display = 'none';
                    
                    const cycles = billingCycleDataEl.textContent.split(",").map(str => str.trim());
                    
                    // Clear existing options
                    billingCycleSelectEl.innerHTML = "";
                    
                    cycles.forEach(cycle => {
                        const [timePart, costPart] = cycle.split(":");
                        const qty = parseInt(timePart.slice(0, -1), 10);
                        const unit = timePart.slice(-1);
                        let label = "";
                        switch (unit) {
                            case "y": label = qty === 1 ? "Annually" : `${qty} years`; break;
                            case "m": label = qty === 1 ? "Monthly" : `${qty} months`; break;
                            case "w": label = qty === 1 ? "Weekly" : `${qty} weeks`; break;
                            case "d": label = qty === 1 ? "Daily" : `${qty} days`; break;
                        }
                        const option = document.createElement("option");
                        option.value = cycle;
                        option.text = `${label} (${currencyPrefix}${parseFloat(costPart).toFixed(2)}${currencySuffix})`;
                        billingCycleSelectEl.appendChild(option);
                    });

                    // Preselect first item and UPDATE THE PRODUCT PRICE
                    if (cycles.length > 0) {
                        billingCycleSelectEl.selectedIndex = 0;
                        const [, firstCostPart] = cycles[0].split(":");
                        const firstCostNum = parseFloat(firstCostPart).toFixed(2);
                        
                        // Update the product price in this container (with currency format)
                        if (productPriceEl) {
                            productPriceEl.textContent = `${currencyPrefix}${firstCostNum}${currencySuffix}`;
                        }
                    }
                    
                    // Add event listener to update price when selection changes
                    billingCycleSelectEl.addEventListener("change", () => {
                        const [, costPart] = billingCycleSelectEl.value.split(":");
                        const costNum = parseFloat(costPart).toFixed(2);
                        
                        // Update the product price in this container (with currency format)
                        if (productPriceEl) {
                            productPriceEl.textContent = `${currencyPrefix}${costNum}${currencySuffix}`;
                        }
                    });
                }
            };

            // Find all product containers and populate their billing cycle selects
            const productContainers = document.querySelectorAll("[pc='product']");
            if (productContainers.length > 0) {
                // Multiple products structure - populate each one
                productContainers.forEach(container => {
                    populateBillingCycleSelect(container);
                });
            } else {
                // Legacy single product structure - populate from document
                populateBillingCycleSelect(document);
            }

            // Add event listeners for popup (only in modal mode)
            if (subscriptionMode !== 'embed') {
                const closeButton = document.querySelector('.subscription-close-btn');
                const cancelButton = document.querySelector('[pc="cancel-subscription"]');
                
                if (closeButton) {
                    closeButton.addEventListener("click", () => {
                        const popup = document.querySelector('[pc="subscription"]') as HTMLElement;
                        if (popup) {
                            popup.style.display = "none";
                        }
                    });
                }
                
                if (cancelButton) {
                    cancelButton.addEventListener("click", () => {
                        const popup = document.querySelector('[pc="subscription"]') as HTMLElement;
                        if (popup) {
                            popup.style.display = "none";
                        }
                    });
                }
            }
        }
        return true;
    }
    return false;
};

/**
 * Starts the subscription process for a product
 * @param {any} productData Product data for subscription
 * @param {HTMLElement} productContext Optional context element to read product-specific data from (for multiple products on same page)
 */
export const startSubscription = async (productData: any, productContext?: HTMLElement) => {

    localStorage.removeItem('subscription');
    localStorage.removeItem('shippingMethods');
    // Optionally clear other related keys if needed
    // localStorage.removeItem('order-id'); // Uncomment if you want to reset order id

    // Determine subscription mode
    const subscriptionFormEl = document.querySelector('[pc-sub="form"]');
    const subscriptionMode = subscriptionFormEl?.getAttribute('pc-sub-mode') || 'modal';

    let subscriptionElement: HTMLElement | null = null;
    
    if (subscriptionMode === 'embed') {
        // For embed mode, get or insert content in the wrapper
        let wrapper = document.querySelector('[pc="subscription-wrapper"]');
        if (!wrapper || !wrapper.hasChildNodes()) {
            // Get the HTML content from the API
            const subscriptionHTML = await getSubscriptionTemplate();
            subscriptionElement = insertSubscriptionContent(subscriptionHTML, 'embed');
        } else {
            subscriptionElement = wrapper.firstElementChild as HTMLElement;
        }
    } else {
        // For modal mode, handle popup display
        let popup = document.querySelector('[pc="subscription"]') as HTMLElement;
        if (!popup) {
            // Get the HTML content from the API
            const subscriptionHTML = await getSubscriptionTemplate();
            subscriptionElement = insertSubscriptionContent(subscriptionHTML, 'modal');
            popup = subscriptionElement as HTMLElement;
        } else {
            subscriptionElement = popup;
        }
        
        // Show the popup for modal mode
        if (popup) {
            popup.style.display = "flex";
            
            setTimeout(() => {
                document.addEventListener('click', handleClose);
            }, 1000);


            //configure close button
            const closeButtonEl = document.querySelector('[pc="close"]');
            if (closeButtonEl) {
                closeButtonEl.addEventListener('click', (event) => {
                    event.stopPropagation(); // Prevent the click from bubbling up to the document
                    handleClose();
                });
            }

            const backgroundCloseEl = document.querySelector('[pc="subscription-background"]');
            if (backgroundCloseEl) {
                backgroundCloseEl.addEventListener('click', (event) => {
                    event.stopPropagation(); // Prevent the click from bubbling up to the document
                    handleClose();
                });
            }
        }
    }
    
    if (subscriptionElement) {
        
        /* SHOW COUPON FORM AGAIN
        ================================================*/
        const coupon_form = document.querySelector('[pc="discount-form"]');
        coupon_form?.classList.remove('hidden');
         // remove pc="active-coupon" 
        const activeCouponEl = document.querySelector('[pc="active-coupon"]');
        if (activeCouponEl) {
            activeCouponEl.remove();
        }
        /*================================================*/

        // --- Clear all form fields in the subscription content ---
        const formContainer = subscriptionElement.querySelector('[pc="subscription-form"]');
        if (formContainer) {
            // Find all input, textarea, and select elements within the form container
            const inputElements = formContainer.querySelectorAll('input, textarea, select');
            inputElements.forEach((el: any) => {
                if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
                    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
                        el.checked = false;
                    } else if (el instanceof HTMLInputElement && el.type === 'submit') {
                        // Skip submit buttons - don't clear their value as it contains the button text
                        return;
                    } else {
                        el.value = '';
                    }
                } else if (el instanceof HTMLSelectElement) {
                    el.selectedIndex = 0;
                }
            });
        }

        // Hide subscription status element and ensure subscription form is visible
        const subscriptionStatusEl = subscriptionElement.querySelector('[pc="subscription-status"]') as HTMLElement;
        const subscriptionFormEl = subscriptionElement.querySelector('[pc="subscription-form"]') as HTMLElement;
        
        if (subscriptionStatusEl) {
            subscriptionStatusEl.classList.add("hide_content");
        }
        if (subscriptionFormEl) {
            subscriptionFormEl.classList.remove("hide_content");
        }

        // --- Reset summary price fields ---
        const summaryPrice = subscriptionElement.querySelector('[pc="summary-price"]');
        if (summaryPrice) summaryPrice.textContent = '$0.00';
        const summaryTotal = subscriptionElement.querySelector('[pc="recurrent-amount"]');
        if (summaryTotal) summaryTotal.textContent = '$0.00';

        // --- Continue with normal logic ---
        // Update summary with product data
        if (summaryPrice && productData.product_price) {
            summaryPrice.textContent = `$${parseFloat(productData.product_price).toFixed(2)}`;
        }
        if (summaryTotal && productData.product_price) {
            summaryTotal.textContent = `$${parseFloat(productData.product_price).toFixed(2)}`;
        }

        // Update product name in subscription popup
        // Use productContext if provided, otherwise fall back to document (legacy behavior)
        const productSearchContext = productContext || document;
        const productNameEl = productSearchContext.querySelector('[pc="product-name"]');
        const subProductNameEl = subscriptionElement.querySelector('[pc="product-name"]');
        const closeButtonEl = subscriptionElement.querySelector('[pc="close"]');
        
        if (subscriptionMode === 'embed') {
            if (subProductNameEl) {
                (subProductNameEl as HTMLElement).style.display = 'none';
            }
            if (closeButtonEl) {
                (closeButtonEl as HTMLElement).style.display = 'none';
            }
        } else {
            // Update product name in modal mode
            if (productNameEl && subProductNameEl) {
                subProductNameEl.textContent = productNameEl.textContent;
            }
        }

        // Get the selected billing cycle and add it to productData
        // The billing cycle select is in the product container (background), NOT in the modal
        // We need to read from productContext (the product container) or document
        let billingCycleSelect: HTMLSelectElement | null = null;
        
        // Always read the billing cycle from the product context (background)
        const billingCycleSearchContext = productContext || document;
        billingCycleSelect = billingCycleSearchContext.querySelector('[pc-sub-element]') as HTMLSelectElement;
        
        if (billingCycleSelect && billingCycleSelect.value) {
            productData.billingCycle = billingCycleSelect.value;
            
            // Update next payment date when subscription starts
            updateNextPaymentDate(billingCycleSelect.value);
        }

        // Store product data in the subscription object in localStorage
        // (now always fresh)
        const existingSubscription = JSON.parse(localStorage.getItem('subscription') || '{}');
        const updatedData = {
            order_code: existingSubscription.order_code || generateOrderCode(),
            products: [productData]
        };
        localStorage.setItem('subscription', JSON.stringify(updatedData));

        // Get payment form configuration from backend
        await getPaymentForm(productData);
    }
};

/*===============================================
SET SUBSCRIPTION PAYMENT STATUS
===============================================*/
const setSubscriptionPaymentStatus = (data: any) => {
    const { status, message, order_id = null } = data;
    
    enqueueSnackbar(message, { variant: status });
    
    if (status === 'success') {
        // Call the success handler from Subscription.tsx
        handleSubscriptionSuccess();
        
        // Update order status title if exists
        const orderStatusTitle = document.querySelector('[pc="subscription-status-heading"]');
        if (orderStatusTitle && order_id) {
            orderStatusTitle.textContent = `Subscription # ${order_id} created`;
        }
        
        // Clear subscription data from localStorage
        localStorage.removeItem('subscription');
        localStorage.removeItem('shippingMethods');
    }
};

/*===============================================
BUILD SUBSCRIPTION PAYMENT FORM
===============================================*/
const buildSubscriptionPaymentForm = async (payment_info: any) => {
    if (Object.keys(payment_info).length !== 0) {
        const paymentForm = document.querySelector('[pc="payment-form"]');
        if (paymentForm) {
            // Only render payment form components for Stripe and Square
            // For other payment methods (elavon, authorize_net, test_mode, free),
            // the existing HTML form fields are used directly
            
            if (payment_info.payment_method === 'stripe' || payment_info.payment_method === 'square') {
                // Create or reuse the React root
                if (!subscriptionPaymentFormRoot) {
                    subscriptionPaymentFormRoot = createRoot(paymentForm);
                }
                
                // The payment form components will attach their own click handlers to [pc="add-btn"]
                subscriptionPaymentFormRoot.render(
                    <>
                        {payment_info.payment_method === 'stripe' && (
                            <StripeForm 
                                payment_settings={payment_info.payment_settings} 
                                setPaymentStatus={setSubscriptionPaymentStatus}
                            />
                        )}

                        {payment_info.payment_method === 'square' && (
                            <SquareForm 
                                setPaymentStatus={setSubscriptionPaymentStatus}
                            />
                        )}
                    </>
                );
            }
            // For elavon, authorize_net, test_mode, free:
            // Don't render anything - use existing HTML form fields
            // The handleSubscriptionSubmit in Subscription.tsx handles validation and submission
        }
    }
};

/*===============================================
GET PAYMENT FORM
===============================================*/
export const getPaymentForm = async (additionalData: any) => {
    let subscription_data = JSON.parse(localStorage.getItem('subscription') || '{}');
    
    // If order_code is missing, generate it and save to localStorage
    if (!subscription_data.order_code) {
        subscription_data.order_code = generateOrderCode();
    }
    
    // If total_price is missing, initialize it with the product price
    if (!subscription_data.total_price) {
        const productPrice = subscription_data.products?.[0]?.product_price || 0;
        subscription_data.total_price = parseFloat(productPrice).toFixed(2);
        subscription_data.subtotal = subscription_data.total_price;
    }
    
    // Save updated subscription_data to localStorage
    localStorage.setItem('subscription', JSON.stringify(subscription_data));
    
    ShowLoader(true);
    await ApiRequest({
        url: "/payments/getSubscriptionPaymentForm",
        method: "post",
        body: {
            subscription_data,
        },
        setResponse: async (response: any) => {
            ShowLoader(false);
            if(response.status === 'error') {
                alert(response.message);
                return;
            }
            
            // Store payment method information in the subscription object
            const storedData = localStorage.getItem('subscription');
            let subscriptionData = {};
            
            try {
                if (storedData) {
                    subscriptionData = JSON.parse(storedData);
                }
            } catch (error) {
                console.error('Error parsing subscription data:', error);
            }
            
            // Update subscription with payment info (include payment_settings if available)
            const updatedData = {
                ...subscriptionData,
                payment_info: {
                    payment_method: response.payment_method,
                    payment_settings: response.payment_settings || null
                }
            };
            
            localStorage.setItem('subscription', JSON.stringify(updatedData));
            
            // Build and render the payment form based on payment method
            await buildSubscriptionPaymentForm(updatedData.payment_info);
            
            // Initialize the form (this will set up event listeners and populate fields)
            initializeSubscription();
            
            // If there's any payment method specific configuration needed
            configurePaymentMethod(response.payment_method);
        },
    });
};

/*===============================================
CONFIGURE PAYMENT METHOD
===============================================*/
const configurePaymentMethod = (paymentMethod: string) => {
    switch(paymentMethod) {
        case 'elavon':
            // Add any Elavon-specific configurations here
            break;
        case 'stripe':
            // Add any Stripe-specific configurations here
            break;
        case 'authorize_net':
            // Add any Authorize.Net-specific configurations here
            break;
        case 'test_mode':
            // Add test mode indication
            const testModeIndicator = document.createElement('div');
            testModeIndicator.className = 'test-mode-indicator';
            testModeIndicator.innerHTML = '<span>Test Mode - No actual charges will be made</span>';
            
            const paymentForm = document.querySelector('[pc="payment-form"]');
            if (paymentForm) {
                paymentForm.prepend(testModeIndicator);
            }
            break;
        default:
            // Default configuration
            break;
    }
};

/**
 * Cleanup subscription-related DOM elements
 */
export const cleanupSubscription = () => {
    // Remove event listener when cleaning up
    document.removeEventListener('click', handleClose);
    
    // Cleanup the payment form React root
    if (subscriptionPaymentFormRoot) {
        try {
            subscriptionPaymentFormRoot.unmount();
        } catch (error) {
            console.warn('Error unmounting payment form root:', error);
        }
        subscriptionPaymentFormRoot = null;
    }
    
    // Determine subscription mode
    const subscriptionFormEl = document.querySelector('[pc-sub="form"]');
    const subscriptionMode = subscriptionFormEl?.getAttribute('pc-sub-mode') || 'modal';
    
    if (subscriptionMode === 'embed') {
        // For embed mode, clear the wrapper
        const wrapper = document.querySelector('[pc="subscription-wrapper"]');
        if (wrapper) {
            wrapper.innerHTML = '';
        }
    } else {
        // For modal mode, remove the popup
        const subscriptionPopup = document.querySelector('[pc="subscription"]');
        if (subscriptionPopup) {
            try {
                document.body.removeChild(subscriptionPopup);
            } catch (error) {
                console.warn('Error removing subscription popup:', error);
            }
        }
    }
    
    // Clear subscription data from localStorage
    localStorage.removeItem('subscription');
};

